import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Col,
  Collapse,
  CollapseProps,
  Form,
  Input,
  Modal,
  Progress,
  Radio,
  Row,
  Select,
  Spin,
  Steps,
  Tag,
  Typography,
} from "antd";
import styles from "./dashboard.module.css";
import Text, { LanguageContext } from "../../context/language-context";
import { FullscreenModal } from "../../components/fullscreen-modal";
import {
  CreateForm,
  ExportSinglePdf,
  GetUserForms,
  RegisterOtherUser,
  SignUp,
  getTimeline,
  updateFormStatus,
} from "../../utils/api.constant";

import { FaCheck } from "react-icons/fa";
import { IoMdCheckmark } from "react-icons/io";
import {
  eFORM_FOR,
  eFORMSTATUS,
  eLANGUAGE,
  eResultCode,
  eSTAGE,
  eSTAPPER_STATUS,
  eTimeline,
  eUSER_FORM_TYPE,
  eUSER_FORM_TYPE_NAME,
  eUserType,
} from "../../utils/enum";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import useFetch from "../../hooks/useFetch";
import useToast from "../../hooks/useToast";
import {
  TDashboardPayload,
  TLandingPageUserData,
  TLandingPageUserRegistration,
  TPreviewState,
} from "../../types";
import { UserContext } from "../../context";
import EncryptUtils from "../../utils/encrypt";
import { getNumberInLanguage } from "../../utils/helperFunctions";
import { TResponseModel } from "../../types/config";
import TeacherPartOneCurrent from "../forms/current/teacher/teacher_pt_1_2026";
import TeacherPartTwoCurrent from "../forms/current/teacher/teacher_pt_2_2026";
import StudentPartTwoCurrent from "../forms/current/student/student_pt_2_2026";
import SchoolPartTwoCurrent from "../forms/current/school/school_pt_2_2026";
import Loader from "../../components/loader/loader";
import { LoaderContext } from "../../context/loader";
import { allowOnlyNumericInput_onKeyDown } from "../../utils/input.validator";
import { Regex } from "../../utils/validation";
import DashboardGuideline from "../../components/dashboardGuideline";
import { CURRENT_YEAR } from "../../utils/constants";
import SchoolPartOneCurrent from "../forms/current/school/school_pt_1_2026";
import StudentPartOneCurrent from "../forms/current/student/student_pt_1_2026";

type Language = "english" | "hindi" | "sanskrit";

// ==================== CONSTANTS ====================

const LANG_OPTIONS = [
  { label: "English", value: "en" },
  { label: "हिन्दी", value: "hi" },
  // { label: "संस्कृतम्", value: "sk" },
];

const FORM_TYPES = [
  eUSER_FORM_TYPE.STUDENT,
  eUSER_FORM_TYPE.TEACHER,
  eUSER_FORM_TYPE.SCHOOL,
];

// Form type to route mapping
const FORM_ROUTES: Record<number, string> = {
  [eUSER_FORM_TYPE.STUDENT]: "/student-form",
  [eUSER_FORM_TYPE.TEACHER]: "/teacher-form",
  [eUSER_FORM_TYPE.SCHOOL]: "/school-form",
};

// Form component mapping for preview modal
interface GetFormComponentParams {
  userType: number;
  stage: number;
  formId: string;
  currentFormFor?: string;
  currentUserId?: string;
}

const getFormComponent = ({
  userType,
  stage,
  formId,
  currentFormFor,
  currentUserId,
}: GetFormComponentParams) => {
  const isStage1 = stage === eSTAGE.STAGE1;

  const components = {
    [eUSER_FORM_TYPE.STUDENT]: isStage1 ? (
      <StudentPartOneCurrent
        disabled={true}
        currentFormId={formId}
        currentUserId={currentUserId}
        currentFormFor={currentFormFor}
      />
    ) : (
      <StudentPartTwoCurrent
        disabled={true}
        currentFormId={formId}
        currentUserId={currentUserId}
        currentFormFor={currentFormFor}
      />
    ),
    [eUSER_FORM_TYPE.TEACHER]: isStage1 ? (
      <TeacherPartOneCurrent
        disabled={true}
        currentFormId={formId}
        currentUserId={currentUserId}
        currentFormFor={currentFormFor}
      />
    ) : (
      <TeacherPartTwoCurrent
        disabled={true}
        currentFormId={formId}
        currentUserId={currentUserId}
        currentFormFor={currentFormFor}
      />
    ),
    [eUSER_FORM_TYPE.SCHOOL]: isStage1 ? (
      <SchoolPartOneCurrent
        disabled={true}
        currentFormId={formId}
        currentFormFor={currentFormFor}
        currentUserId={currentUserId}
      />
    ) : (
      <SchoolPartTwoCurrent
        disabled={true}
        currentFormId={formId}
        currentUserId={currentUserId}
        currentFormFor={currentFormFor}
      />
    ),
  };

  return components[userType] || null;
};

// ==================== HELPER FUNCTIONS ====================

const buildFormQueryParams = (
  id: number,
  formFor: number,
  currentStage: number,
  stage: number,
  userId?: number,
): URLSearchParams => {
  const params: any = {
    id: EncryptUtils.encrypt(String(id)),
    ff: EncryptUtils.encrypt(String(formFor)),
    cs: EncryptUtils.encrypt(String(currentStage)),
    stage: EncryptUtils.encrypt(String(stage)),
  };
  if (userId) {
    params.ui = EncryptUtils.encrypt(String(userId));
  }
  return new URLSearchParams(params);
};

interface NavigateToFormParams {
  navigate: any;
  formType: number;
  formId: number;
  formFor: number;
  currentStage: number;
  stage: number;
  userId?: number;
}

const navigateToForm = ({
  navigate,
  formType,
  formId,
  formFor,
  currentStage,
  stage,
  userId,
}: NavigateToFormParams) => {
  const route = FORM_ROUTES[formType];
  if (!route) return;

  const queryParams = buildFormQueryParams(
    formId,
    formFor,
    currentStage,
    stage,
    userId,
  );
  navigate(`${route}?${queryParams.toString()}`);
};

// ==================== MAIN COMPONENT ====================

function Dashboard() {
  // Context
  const { userLanguage, userLanguageChange, dictionary } =
    useContext(LanguageContext);
  const userData = useContext(UserContext);
  const contextForLoader = useContext(LoaderContext);
  const { isLoading, setIsLoading } = contextForLoader as any;

  // Hooks
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // State - Form Selection
  const [selectedFormType, setSelectedFormType] = useState<number | null>(null);
  const [formFor, setFormFor] = useState<number | null>(null);
  const [acknowledgementVerify, setAcknowledgementVerify] = useState(false);

  // State - User Data
  const [userYourselfData, setUserYourselfData] =
    useState<TLandingPageUserData>();
  const [userOtherData, setUserOtherData] = useState<TLandingPageUserData[]>();
  const [userAllData, setUserAllData] = useState<TLandingPageUserData[]>();
  const [timelineData, setTimelineData] = useState<any[]>([]);

  // State - UI
  const [activeKey, setActiveKey] = useState<number | number[]>(0);
  const [stepperDirection, setStepperDirection] = useState<
    "vertical" | "horizontal"
  >(window.innerWidth < 1250 ? "vertical" : "horizontal");

  // State - Preview Modal (consolidated)
  const [previewData, setPreviewData] = useState<TPreviewState | null>(null);
  const [userDataForPreviewModal, setUserDataForPreviewModal] =
    useState<TLandingPageUserData | null>();

  // ==================== API FUNCTIONS ====================

  const getUserMappedForm = async () => {
    setIsLoading(true);
    try {
      const payload = {
        data: {
          year: CURRENT_YEAR,
        },
      };
      const response = await post(GetUserForms, payload);
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        setUserYourselfData(
          data.find(
            (item: TLandingPageUserData) => item.formFor === eFORM_FOR.YOURSELF,
          ),
        );
        setUserOtherData(
          data.filter(
            (item: TLandingPageUserData) => item.formFor === eFORM_FOR.OTHER,
          ),
        );
        setUserAllData(data);
      }
    } catch (error) {
      console.error("Get User Mapped Error: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTimelines = async () => {
    setIsLoading(true);
    try {
      const response = await post(getTimeline, {});
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        setTimelineData(data);
      }
    } catch (error) {
      console.error("Get Timelines Error: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Main form creation/navigation handler
   * For Part 1: Creates new form via API, gets form ID, navigates
   * For Part 2: Reuses existing form ID, navigates directly (no API call)
   */
  interface HandleFillFormParams {
    userFormType?: number;
    userId?: number;
    existingFormId?: number;
    currentStage?: number;
  }

  const handleFillForm = async ({
    userFormType,
    userId,
    existingFormId,
    currentStage,
  }: HandleFillFormParams = {}) => {
    setIsLoading(true);

    try {
      const formType = userFormType || selectedFormType;
      if (!formType || !formFor) {
        throw new Error("Form type or formFor not selected");
      }

      // Part 2: Reuse existing form ID - just navigate
      if (existingFormId) {
        // If Stage 1 is Approved, update status to Stage 2 In Progress before navigating
        if (currentStage === eFORMSTATUS.STAGE1APPROVED) {
          const requestPayload = {
            data: {
              userFormMappingId: existingFormId,
              userId: userId,
              newStatus: eFORMSTATUS.STAGE2INPROGRESS,
              comment: "",
              year: CURRENT_YEAR,
            },
          };

          const response = await post(updateFormStatus, requestPayload);
          const { dataResponse } = response;
          const { returnCode } = dataResponse;

          if (returnCode === eResultCode.SUCCESS) {
            // Update local state if needed, but navigation will trigger re-fetch on form page
            navigateToForm({
              navigate,
              formType,
              formId: existingFormId,
              formFor,
              currentStage: eFORMSTATUS.STAGE2INPROGRESS,
              stage: eSTAGE.STAGE2,
              userId,
            });
            return;
          }
        }

        navigateToForm({
          navigate,
          formType,
          formId: existingFormId,
          formFor,
          currentStage: eFORMSTATUS.STAGE2INPROGRESS,
          stage: eSTAGE.STAGE2,
          userId,
        });
        return;
      }

      // Part 1: Create new form
      const payload: { data: TDashboardPayload } = {
        data: {
          userFormType: formType,
          formFor: formFor,
          currentStage: eFORMSTATUS.STAGE1INPROGRESS,
          selectedLanguage:
            userLanguage === "en"
              ? eLANGUAGE.ENGLISH
              : userLanguage === "hi"
                ? eLANGUAGE.HINDI
                : eLANGUAGE.SANSKRIT,
        },
      };

      if (userId) {
        payload.data.userId = userId;
      }

      const response = await post(CreateForm, payload);
      const { dataResponse, data } = response;
      const { returnCode, description } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        navigateToForm({
          navigate,
          formType: data.userFormType,
          formId: data.id,
          formFor: data.formFor,
          currentStage: data.currentStage,
          stage: eSTAGE.STAGE1,
          userId: data.userId || userId,
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: description,
        });
      }
    } catch (error) {
      console.error(error);
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.leftBottom,
        content: "Something went wrong",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserRegistrationForm = async (values: any) => {
    setIsLoading(true);
    try {
      const payload: { data: TLandingPageUserRegistration } = {
        data: {
          fullName: values.fullName,
          mobileNumber: values.mobileNumber,
          emailId: values.emailId.toLowerCase(),
          aadhaarCardNumber: values.aadhaarCardNumber,
          isOtherUser: 1,
          password: EncryptUtils.encrypt(values.mobileNumber),
        },
      };

      const response: TResponseModel = await post(RegisterOtherUser, payload);
      const { dataResponse, data } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        await handleFillForm({
          userFormType: selectedFormType || undefined,
          userId: parseInt(data.id),
        });
      } else if (returnCode === eResultCode.DB_ERROR) {
        Modal.confirm({
          title: dictionary["CONFIRMATION"] || "Confirmation",
          content:
            dictionary["AADHAAR_OVERWRITE_CONFIRMATION"] ||
            "A different email is already associated with this Aadhaar number. Do you want to overwrite the existing data?",
          okText: dictionary["YES_OVERWRITE"] || "Yes, Overwrite",
          cancelText: dictionary["CANCEL"] || "Cancel",
          onOk: async () => {
            try {
              setIsLoading(true);
              const overwritePayload: { data: TLandingPageUserRegistration } = {
                data: {
                  ...payload.data,
                  overwriteEmail: 1,
                },
              };
              const retryResponse: TResponseModel = await post(
                RegisterOtherUser,
                overwritePayload,
              );
              const { dataResponse: retryDataResponse, data: retryData } =
                retryResponse;
              if (retryDataResponse.returnCode === eResultCode.SUCCESS) {
                await handleFillForm({
                  userFormType: selectedFormType || undefined,
                  userId: parseInt(retryData.id),
                });
              } else {
                onShowToast({
                  type: ToastType.error,
                  title: <CloseOutlined />,
                  position: ToastOpen.leftBottom,
                  content: retryDataResponse.description,
                });
              }
            } catch (err) {
              console.error("Dashboard overwrite error", err);
            } finally {
              setIsLoading(false);
            }
          },
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: description,
        });
      }
    } catch (error) {
      console.error("Dashboard", error);
    } finally {
      form.resetFields();
      setIsLoading(false);
    }
  };

  const handleOpenExternal = async (filePath: string) => {
    const fileURL = `${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}/${filePath}`;
    window.open(fileURL, "_blank");
    try {
      const response = await fetch(fileURL, { mode: "cors" });
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filePath.split("/").pop() || "file.pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("PDF download failed:", error);
    }
  };

  const exportSinglePdfOrBinder = async () => {
    setIsLoading(true);
    try {
      const payload = {
        data: {
          id: userDataForPreviewModal?.id,
          userFormType: userDataForPreviewModal?.userFormType,
          isAffidavit: true,
          isDocument: false,
          isPtOne: previewData?.stage === eSTAGE.STAGE1,
        },
      };
      const response = await post(ExportSinglePdf, payload);
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <FaCheck />,
          position: ToastOpen.leftBottom,
          content:
            dictionary["PDF_DOWNLOAD_SUCCESS"] || "PDF downloaded successfully",
        });
        handleOpenExternal(data.filePath);
      }
    } catch (error) {
      console.error("Dashboard Export PDF Error: ", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.leftBottom,
        content:
          dictionary["SOMETHING_WENT_WRONG"] ||
          "Something went wrong. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ==================== HELPER FUNCTIONS ====================

  const handlePreview = (
    stage: number,
    userType: number,
    formFor: number,
    userId: number,
    id: number | null,
  ) => {
    if (!id) return;
    setPreviewData({
      formId: EncryptUtils.encrypt(id.toString()),
      stage,
      userType,
      formFor: EncryptUtils.encrypt(String(formFor)),
      userId: EncryptUtils.encrypt(String(userId)),
    });
  };

  const formatTimelineDate = (timelineName: string) => {
    const timelineItem = timelineData?.find(
      (item) => item.name === timelineName,
    );
    if (!timelineItem) return "";

    const startDate = new Date(timelineItem.startDate);
    const endDate = new Date(timelineItem.endDate);

    const formatDate = (date: Date) => {
      const day = getNumberInLanguage(date.getDate());
      const month = Text({
        tid: `MONTH_${date.getMonth() + 1}`,
        def: `${date.getMonth() + 1}`,
      });
      const year = getNumberInLanguage(date.getFullYear());
      return `${day} ${month} ${year}`;
    };

    return startDate.getTime() === endDate.getTime()
      ? formatDate(startDate)
      : `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const getStepperItems = (item: TLandingPageUserData) => [
    {
      title:
        item.currentStage <= eFORMSTATUS.STAGE1INPROGRESS
          ? Text({ tid: "STAGE_1_IN_PROGRESS", def: "Stage 1 : In Progress" })
          : Text({ tid: "STAGE_1_SUBMITTED", def: "Stage 1 : Submitted" }),
      description: formatTimelineDate(eTimeline.PT1_SUBMISSION),
      status:
        item.currentStage === eFORMSTATUS.STAGE1INPROGRESS
          ? eSTAPPER_STATUS.IN_PROGRESS
          : eSTAPPER_STATUS.DONE,
    },
    {
      title:
        item.currentStage === eFORMSTATUS.STAGE1REJECTED
          ? Text({
              tid: "STAGE_1_FORM_REJECTED",
              def: "Stage 1 : Form Rejected",
            })
          : Text({
              tid: "STAGE_1_FORM_APPROVAL",
              def: "Stage 1 : Form Approval",
            }),
      description: formatTimelineDate(eTimeline.PT1_APPROVAL),
      status:
        item.currentStage >= eFORMSTATUS.STAGE1APPROVED
          ? eSTAPPER_STATUS.DONE
          : item.currentStage >= eFORMSTATUS.STAGE1REJECTED
            ? eSTAPPER_STATUS.REJECT
            : eSTAPPER_STATUS.DUE,
    },
    {
      title:
        item.currentStage <= eFORMSTATUS.STAGE2INPROGRESS
          ? Text({ tid: "STAGE_2_IN_PROGRESS", def: "Stage 2 : In Progress" })
          : Text({ tid: "STAGE_2_SUBMITTED", def: " Stage 2 : Submitted" }),
      description: formatTimelineDate(eTimeline.PT2_SUBMISSION),
      status:
        item.currentStage >= eFORMSTATUS.STAGE2SUBMITTED
          ? eSTAPPER_STATUS.DONE
          : item.currentStage >= eFORMSTATUS.STAGE2INPROGRESS
            ? eSTAPPER_STATUS.IN_PROGRESS
            : eSTAPPER_STATUS.DUE,
    },
    {
      title:
        item.currentStage === eFORMSTATUS.STAGE2REJECTED
          ? Text({
              tid: "STAGE_2_FORM_REJECTED",
              def: "Stage 2 : Form Rejected",
            })
          : Text({
              tid: "STAGE_2_FORM_APPROVAL",
              def: "Stage 2 : Form Approval",
            }),
      description: formatTimelineDate(eTimeline.PT2_APPROVAL),
      status:
        item.currentStage >= eFORMSTATUS.STAGE2APPROVED
          ? eSTAPPER_STATUS.DONE
          : item.currentStage >= eFORMSTATUS.STAGE2REJECTED
            ? eSTAPPER_STATUS.REJECT
            : eSTAPPER_STATUS.DUE,
    },
    {
      title:
        item.userFormType === eUSER_FORM_TYPE.SCHOOL
          ? Text({ tid: "OBSERVATION_STAGE", def: "Observation Stage" })
          : Text({ tid: "INTERVIEW_STAGE", def: "Interview Stage" }),
      description: formatTimelineDate(eTimeline.INTERVIEW_STAGE),
      status:
        item.currentStage >= eFORMSTATUS.INTERVIEWSTAGE
          ? eSTAPPER_STATUS.DONE
          : eSTAPPER_STATUS.DUE,
    },
    {
      title:
        item.currentStage === eFORMSTATUS.STAGE2SELECTED
          ? Text({ tid: "FORM_SELECTED", def: "Form Selected" })
          : Text({ tid: "SELECTION_STATUS", def: "Selection Status" }),
      description: formatTimelineDate(eTimeline.SELECTION_DATE),
      status:
        item.currentStage >= eFORMSTATUS.STAGE2SELECTED
          ? eSTAPPER_STATUS.DONE
          : eSTAPPER_STATUS.DUE,
    },
    {
      title: Text({ tid: "AWARD_DATE", def: "Award" }),
      description: formatTimelineDate(eTimeline.AWARD_DATE),
      status:
        item.currentStage >= eFORMSTATUS.STAGE2SELECTED
          ? eSTAPPER_STATUS.DONE
          : eSTAPPER_STATUS.DUE,
    },
  ];

  // ==================== RENDER HELPERS ====================

  const renderFormStageButtons = (
    item: TLandingPageUserData,
    stage: number,
  ) => {
    const isStage1 = stage === eSTAGE.STAGE1;
    const isStage2 = stage === eSTAGE.STAGE2;

    const canContinueStage1 =
      isStage1 && item.currentStage < eFORMSTATUS.STAGE1SUBMITTED;
    const canFillStage2 =
      isStage2 && item.currentStage === eFORMSTATUS.STAGE1APPROVED;
    const canContinueStage2 =
      isStage2 &&
      item.currentStage < eFORMSTATUS.STAGE2SUBMITTED &&
      item.currentStage > eFORMSTATUS.STAGE1APPROVED;

    return (
      <div className={styles.filledFormStepContainerItemButtonContainer}>
        {canContinueStage1 && (
          <Button
            onClick={() =>
              navigateToForm({
                navigate,
                formType: item.userFormType,
                formId: item.id as number,
                formFor: item.formFor,
                currentStage: item.currentStage,
                stage: eSTAGE.STAGE1,
                userId: item.userId,
              })
            }
          >
            {Text({ tid: "CONTINUE", def: "Continue" })}
          </Button>
        )}

        {canFillStage2 && (
          <Button
            type="primary"
            onClick={() =>
              handleFillForm({
                userFormType: item.userFormType,
                userId: item.userId || undefined,
                existingFormId: item.id as number,
                currentStage: item.currentStage,
              })
            }
          >
            {Text({ tid: "fill_form", def: "Fill Form" })}
          </Button>
        )}

        {canContinueStage2 && (
          <Button
            onClick={() =>
              navigateToForm({
                navigate,
                formType: item.userFormType,
                formId: item.id as number,
                formFor: item.formFor,
                currentStage: item.currentStage,
                stage: eSTAGE.STAGE2,
                userId: item.userId,
              })
            }
          >
            {Text({ tid: "CONTINUE", def: "Continue" })}
          </Button>
        )}

        <Button
          onClick={() => {
            handlePreview(
              stage,
              item.userFormType,
              item.formFor,
              item.userId,
              item.id,
            );
            setUserDataForPreviewModal(item);
          }}
        >
          {Text({ tid: "PREVIEW", def: "Preview" })}
        </Button>
      </div>
    );
  };

  // ==================== ACCORDION ITEMS ====================

  const accordionItems: CollapseProps["items"] =
    userAllData
      ?.filter((item) => formFor === item.formFor)
      .map((item, index) => ({
        key: `${index}`,
        label: (
          <div className={styles.landingPageAccordionHeader}>
            {item.userName || ""} : {""}
            {item?.applicationNumber ? `(${item.applicationNumber}) ` : ""}
            {Text({
              tid: eUSER_FORM_TYPE_NAME[item.userFormType],
              def: ".",
            })}
          </div>
        ),
        children: (
          <div className={styles.filledForm}>
            <div className={styles.filledFormStepContainer}>
              {/* Stage 1 */}
              <div className={styles.filledFormStepContainerItem}>
                <Typography.Title level={5}>
                  {Text({
                    tid:
                      item.userFormType === eUSER_FORM_TYPE.SCHOOL
                        ? "SCHOOL_PART_1_FORM_HEADING"
                        : item.userFormType === eUSER_FORM_TYPE.TEACHER
                          ? "TEACHER_PART_1_FORM_HEADING"
                          : "STUDENT_PART_1_FORM_HEADING",
                    def: ".",
                  })}
                </Typography.Title>
                {renderFormStageButtons(item, eSTAGE.STAGE1)}
              </div>

              {/* Stage 2 - Only show if approved for stage 2 */}
              {item.currentStage >= eFORMSTATUS.STAGE1APPROVED && (
                <>
                  <div className="line"></div>
                  <div className={styles.filledFormStepContainerItem}>
                    <Typography.Title level={5}>
                      {Text({
                        tid:
                          item.userFormType === eUSER_FORM_TYPE.SCHOOL
                            ? "SCHOOL_PART_2_FORM_HEADING"
                            : item.userFormType === eUSER_FORM_TYPE.TEACHER
                              ? "TEACHER_PART_2_FORM_HEADING"
                              : "STUDENT_PART_2_FORM_HEADING",
                        def: ".",
                      })}
                    </Typography.Title>
                    {renderFormStageButtons(item, eSTAGE.STAGE2)}
                  </div>
                </>
              )}
            </div>

            {/* Stepper */}
            <Typography.Title level={3} className="mb-30">
              {Text({ tid: "PROCESS_OF_BHARATATMA_PURASKAR", def: "." })}
            </Typography.Title>
            <Steps
              direction={stepperDirection}
              responsive
              labelPlacement="vertical"
              percent={100}
              items={getStepperItems(item)}
            />
          </div>
        ),
      })) || [];

  // ==================== EFFECTS ====================

  useEffect(() => {
    getTimelines();
    getUserMappedForm();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setStepperDirection(window.innerWidth < 1250 ? "vertical" : "horizontal");
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (userLanguage === "hi") {
      document.body.classList.add("hindi-label");
    } else {
      document.body.classList.remove("hindi-label");
    }
  }, [userLanguage]);

  useEffect(() => {
    if (!acknowledgementVerify) {
      form.resetFields();
    }
  }, [acknowledgementVerify]);

  // ==================== RENDER ====================

  const shouldShowFormTypeButtons =
    formFor && (formFor === eFORM_FOR.OTHER || !userYourselfData);

  const shouldShowEligibility =
    selectedFormType && formFor && shouldShowFormTypeButtons;

  const shouldShowSubmitSection = selectedFormType && acknowledgementVerify;

  const formForOptions =
    userData.user?.userType === eUserType.ADMIN
      ? [eFORM_FOR.OTHER]
      : [eFORM_FOR.YOURSELF, eFORM_FOR.OTHER];

  return (
    <div className={`${styles.dashboard} dashboard`}>
      {/* Language Section - Commented out: language change feature temporarily disabled */}
      <div className={styles.languageSection}>
        <div className={styles.radioGroup}>
          {LANG_OPTIONS.map((language) => (
            <label key={language.value} className="mb-0">
              <input
                value={language.value as Language}
                type="radio"
                name="lang"
                checked={userLanguage === language.value}
                onChange={(e) => {
                  userLanguageChange(e.target.value as any);
                  localStorage.setItem("rcml-lang", e.target.value);
                }}
              />
              <span>{language.label}</span>
            </label>
          ))}
        </div>
        <div className={styles.subLine}>
          <Typography.Text className="mb-0">
            {Text({
              tid: "ls_sub_heading",
              def: "Please fill the form only in selected language, otherwise your form will not be accepted.",
            })}
          </Typography.Text>
        </div>
      </div>

      {/* Application Type Section */}
      <div className={styles.applicationTypeSection}>
        <Typography.Text>
          {Text({
            tid: "ls_form_type_heading",
            def: "For whom are you making the application?",
          })}
        </Typography.Text>
        <div className={styles.radioGroup}>
          {formForOptions.map((type: number) => (
            <label key={type}>
              <input
                value={type}
                type="radio"
                name="type"
                checked={formFor === type}
                onChange={(e) => setFormFor(Number(e.target.value))}
              />
              <span>
                {Text({
                  tid: type === eFORM_FOR.YOURSELF ? "yourself" : "others",
                  def:
                    type === eFORM_FOR.YOURSELF
                      ? "Yourself"
                      : "For Someone Else",
                })}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* User Type Selection Buttons */}
      {shouldShowFormTypeButtons && (
        <div className={styles.userButtonSection}>
          {FORM_TYPES.map((userType: number) => (
            <button
              key={userType}
              type="button"
              className={
                selectedFormType === userType
                  ? styles.activeButton
                  : styles.button
              }
              onClick={() => setSelectedFormType(userType)}
            >
              {selectedFormType === userType && (
                <IoMdCheckmark
                  size={30}
                  style={{
                    borderRadius: "50%",
                    color: "#fff",
                    background: "transparent",
                    border: "2px solid #eee",
                    padding: "5px",
                  }}
                />
              )}
              <span>
                {Text({
                  tid: eUSER_FORM_TYPE_NAME[userType],
                  def: "",
                })}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Eligibility Criteria Section */}
      {shouldShowEligibility && (
        <div className={styles.eligibilitySection}>
          <DashboardGuideline userType={selectedFormType} />
          <div className={styles.checkboxSection}>
            <label>
              <input
                type="checkbox"
                checked={acknowledgementVerify}
                onChange={(e) => setAcknowledgementVerify(e.target.checked)}
              />
              <span>
                {Text({
                  tid: "ls_agreement",
                  def: "I have read, understood and agree with the eligibility criteria and guidelines listed above.",
                })}
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Submit Button Section */}
      {shouldShowSubmitSection && (
        <div className={styles.submitButtonSection}>
          {/* For Other User - Registration Form */}
          {formFor === eFORM_FOR.OTHER && (
            <Form
              onFinish={handleUserRegistrationForm}
              form={form}
              name="register"
              scrollToFirstError
              layout="vertical"
            >
              <div className={styles.landingPageUserRegistrationSectionContent}>
                <Row>
                  <Col xs={24} lg={12} xl={8}>
                    <Form.Item
                      label={
                        selectedFormType === eUSER_FORM_TYPE.SCHOOL
                          ? Text({
                              tid: "INSTITUTION_HEAD_FULL_NAME",
                              def: "Institute Head Full Name",
                            })
                          : Text({ tid: "full_name", def: "Full name" })
                      }
                      name="fullName"
                      rules={[
                        {
                          required: true,
                          message: Text({
                            tid: "REQUIRED",
                            def: "This field is required",
                          }),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} lg={12} xl={8}>
                    <Form.Item
                      label={Text({
                        tid: "mobile_number",
                        def: "Mobile number",
                      })}
                      name="mobileNumber"
                      rules={[
                        {
                          required: true,
                          message: Text({
                            tid: "REQUIRED",
                            def: "This field is required",
                          }),
                        },
                        {
                          pattern: Regex.MOBILENO,
                          message: Text({
                            tid: "mobile_number_validation",
                            def: "Enter a valid 10-digit mobile number",
                          }),
                        },
                      ]}
                    >
                      <Input
                        maxLength={10}
                        onKeyDown={allowOnlyNumericInput_onKeyDown}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} lg={12} xl={8}>
                    <Form.Item
                      label={Text({
                        tid: "email_address",
                        def: "Email address",
                      })}
                      name="emailId"
                      rules={[
                        {
                          required: true,
                          message: Text({
                            tid: "REQUIRED",
                            def: "This field is required",
                          }),
                        },
                        {
                          type: "email",
                          message: Text({
                            tid: "email_validation",
                            def: "Invalid email",
                          }),
                        },
                      ]}
                    >
                      <Input type="email" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} lg={12} xl={8}>
                    <Form.Item
                      name="aadhaarCardNumber"
                      label={Text({
                        tid:
                          selectedFormType !== eUSER_FORM_TYPE.SCHOOL
                            ? "aadhaar_card_number"
                            : "ORGANIZATION_MEMBER",
                        def: "Aadhaar Card Number",
                      })}
                      rules={[
                        {
                          required: true,
                          message: Text({
                            tid: "REQUIRED",
                            def: "This field is required",
                          }),
                        },
                        {
                          pattern: /^[2-9]{1}[0-9]{11}$/,
                          message: Text({
                            tid: "aadhaar_number_must_be_12_digits",
                            def: "Aadhaar number must be 12 digits!",
                          }),
                        },
                      ]}
                    >
                      <Input
                        maxLength={12}
                        onKeyDown={allowOnlyNumericInput_onKeyDown}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
              <div
                className={`mt-label ${styles.landingPageUserRegistrationSectionFooter}`}
              >
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={!acknowledgementVerify}
                >
                  {Text({ tid: "fill_form", def: "Fill Form" })}
                </Button>
              </div>
            </Form>
          )}

          {/* For Yourself - Direct Button */}
          {formFor === eFORM_FOR.YOURSELF && !userYourselfData && (
            <Button
              type="primary"
              onClick={() => handleFillForm({})}
              disabled={!acknowledgementVerify}
              className={styles.submitButton}
            >
              {Text({ tid: "fill_form", def: "Fill Form" })}
            </Button>
          )}
        </div>
      )}

      {/* Accordion for Filled Forms */}
      {formFor && (
        <div className={styles.timelineSection}>
          {accordionItems.length !== 0 && (
            <Typography.Title level={3}>
              {Text({ tid: "FILLED_FORM", def: "FILLED FORM" })}
            </Typography.Title>
          )}
          <Collapse
            accordion
            items={accordionItems}
            activeKey={activeKey}
            onChange={(key) => setActiveKey(Number(key))}
          />
        </div>
      )}

      {/* Preview Modal - Fullscreen */}
      <FullscreenModal
        open={!!previewData}
        onClose={() => {
          setPreviewData(null);
          setUserDataForPreviewModal(null);
        }}
        loading={isLoading}
        extraFooterButtons={
          <Button
            type="primary"
            onClick={exportSinglePdfOrBinder}
            disabled={isLoading}
          >
            {!isLoading ? (
              Text({ tid: "DOWNLOAD_PDF", def: "Download PDF" })
            ) : (
              <Spin indicator={<LoadingOutlined spin />} size="small" />
            )}
          </Button>
        }
      >
        {previewData
          ? getFormComponent({
              userType: previewData.userType,
              stage: previewData.stage,
              formId: previewData.formId,
              currentFormFor: previewData.formFor,
              currentUserId: previewData.userId,
            })
          : null}
      </FullscreenModal>

      <div className="lastPadding"></div>
    </div>
  );
}

export default Dashboard;
